type ClassConstructor<T, A extends any[] = any[]> = new (...args: A) => T;

export type BuilderType<T> = Builder<T> & {
  [Property in keyof T]: (value: T[Property]) => BuilderType<T>;
};

export class Builder<T> {
  private targetCtor: ClassConstructor<T>;
  private targetCtorArgs: unknown[];
  private propertiesToAssign: Partial<T> = {};

  private constructor(targetCtor: ClassConstructor<T>, ...targetCtorArgs: unknown[]) {
    if (!targetCtor) {
      throw new Error('Target class not provided');
    }
    this.targetCtor = targetCtor;
    this.targetCtorArgs = targetCtorArgs;
  }

  /**
   * @param targetClass The class to build (e.g., AppFilter)
   * @param targetCtorArgs The arguments required by the class's constructor e.g AppFilter('id', 'type', 'prop', 'title', 'placeholder')
   */
  static create<T, A extends unknown[]>(targetClass: ClassConstructor<T, A>, ...targetCtorArgs: A): BuilderType<T> {
    const builder = new Builder(targetClass, ...targetCtorArgs);

    return new Proxy(builder, {
      /**
       * Intercepts property access on the builder.
       * @param builderInstance The real instance of the Builder class (e.g., Builder<AppFilter>).
       * @param property The name of the method being accessed (e.g., 'selectMode' when calling .selectMode('multiple') or 'build').
       * @param proxy The proxy object itself, returned to enable method chaining (e.g., .selectMode('multiple').isLoading(true)).
       */
      get(builderInstance, property: string | symbol, proxy) {
        if (property in builderInstance) {
          return builderInstance[property];
        }

        /**
         * @param value: The argument passed to the setter (e.g., 'multiple' in .selectMode('multiple')).
         */
        return (value: unknown) => {
          builderInstance.propertiesToAssign[property] = value;
          return proxy;
        };
      }
    }) as unknown as BuilderType<T>;
  }



  build(): T {
    try {
      const instance = new this.targetCtor(...this.targetCtorArgs);

      this.applyPropertiesToInstance(instance);

      return instance;

    } catch (error) {
      throw new Error('Error building instance: ' + error);
    }
  }

  private applyPropertiesToInstance(instance: T): void {
    Object.keys(this.propertiesToAssign).forEach((prop) => {
      const key = prop as keyof T;
      const value = this.propertiesToAssign[key];

      if (value === undefined) {
        return;
      }

      instance[key] = value as T[keyof T];
    });
  }
}
